import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet as WalletIcon, Calendar, TrendingUp, Target as TgIcon } from "lucide-react";
import { fmtNum, fmtDate, MONTH_NAMES } from "@/lib/helpers";

export default function Wallet() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  const isPersonel = user?.role === "personel";

  const tick = useRealtimeTick();
  useEffect(() => {
    http.get(`/wallet/me?year=${year}`).then((r) => setData(r.data));
    if (!isPersonel) {
      http.get(`/wallet/summary?year=${year}`).then((r) => setSummary(r.data));
    }
  }, [year, isPersonel, tick]);

  if (!data) return <div className="text-slate-400">Yükleniyor...</div>;

  const monthMap = {};
  (data.entries || []).forEach((e) => { monthMap[e.month] = e; });

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <WalletIcon className="text-amber-400" /> Cüzdan
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {isPersonel ? "Kilitlenen aylarda birikmiş hakedişlerin." : "Yıllık personel cüzdan birikimleri."}
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="bg-slate-900/60 border-slate-700 w-28"><SelectValue /></SelectTrigger>
          <SelectContent>{[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </header>

      <div className="grid gap-4 sm:grid-cols-3 animate-fade-up delay-100">
        <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/15 to-transparent p-6 sm:col-span-2">
          <p className="text-xs uppercase font-mono text-amber-200">{year} Yılı Toplam Birikim</p>
          <p className="font-display text-5xl font-bold text-amber-300 mt-2" data-testid="wallet-total">{fmtNum(data.total)}₺</p>
          <p className="text-xs text-slate-400 mt-2">{data.entries.length} kilitli ay</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-6">
          <p className="text-xs uppercase font-mono text-slate-500">Ortalama / Ay</p>
          <p className="font-display text-3xl font-bold text-blue-300 mt-2">
            {fmtNum(data.entries.length > 0 ? Math.round(data.total / data.entries.length) : 0)}₺
          </p>
          <p className="text-xs text-slate-500 mt-2">kilitli ay başına</p>
        </div>
      </div>

      {/* Monthly timeline */}
      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar size={16} className="text-blue-400" /> Aylık Birikim
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2">
            {MONTH_NAMES.map((name, i) => {
              const m = i + 1;
              const entry = monthMap[m];
              return (
                <div key={m} className={`rounded-xl p-3 text-center border ${entry ? "border-amber-500/30 bg-amber-500/5" : "border-slate-800 bg-slate-900/40"}`} data-testid={`wallet-month-${m}`}>
                  <div className="text-[10px] font-mono text-slate-500">{name.slice(0,3)}</div>
                  <div className={`font-display font-bold text-lg mt-1 ${entry ? "text-amber-300" : "text-slate-600"}`}>
                    {entry ? fmtNum(entry.amount) : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Entries table */}
      {data.entries.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
          <CardHeader>
            <CardTitle className="text-base">Detaylı Kayıtlar</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead>Ay</TableHead>
                  <TableHead>Kilit Tarihi</TableHead>
                  <TableHead className="text-right">Kampanya Sayısı</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((e) => (
                  <TableRow key={`${e.year}-${e.month}`} className="border-slate-800">
                    <TableCell className="font-semibold">{MONTH_NAMES[e.month-1]} {e.year}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-400">{fmtDate(e.locked_at)}</TableCell>
                    <TableCell className="text-right">{(e.earnings || []).length}</TableCell>
                    <TableCell className="text-right font-bold text-amber-300">{fmtNum(e.amount)}₺</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Admin view: all employees yearly summary */}
      {!isPersonel && summary && (
        <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp size={16} className="text-emerald-400" /> {year} Yıllık Birikim — Tüm Personel
              </CardTitle>
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">Toplam: {fmtNum(summary.grand_total)}₺</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead>Personel</TableHead>
                  <TableHead>Şube</TableHead>
                  {MONTH_NAMES.map((m, i) => <TableHead key={i} className="text-right text-xs">{m.slice(0,3)}</TableHead>)}
                  <TableHead className="text-right">Toplam</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.users.map((u) => (
                  <TableRow key={u.user_id} className="border-slate-800">
                    <TableCell className="font-semibold text-sm whitespace-nowrap">{u.full_name}</TableCell>
                    <TableCell className="text-xs text-slate-400 whitespace-nowrap">{u.branch_name}</TableCell>
                    {MONTH_NAMES.map((_, i) => (
                      <TableCell key={i} className="text-right text-xs text-slate-400">
                        {u.months[String(i+1)] ? fmtNum(u.months[String(i+1)]) : "—"}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-bold text-amber-300">{fmtNum(u.total)}₺</TableCell>
                  </TableRow>
                ))}
                {summary.users.length === 0 && (
                  <TableRow><TableCell colSpan={15} className="text-center text-slate-500 py-6">
                    <TgIcon className="mx-auto mb-2" /> Bu yıla ait kilitli bordro yok
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
